import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from '../../components/ui/Button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FileUpload } from 'primereact/fileupload';
import { ColorPicker } from 'primereact/colorpicker';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Card } from '../../components/ui/Card';
import { TabView, TabPanel } from 'primereact/tabview';
import { Divider } from 'primereact/divider';
import { AppTheme } from '../../AppTheme';
import {
  getTemplateById,
  updateTemplateSettings,
  uploadTemplateLogo,
  setDefaultTemplate
} from '../../service/template/TemplateServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheckCircle, faFilePdf, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';

interface TemplateSettings {
  primaryColor: string;
  secondaryColor: string;
  font: string;
  logo: string;
  headerContent: string;
  footerContent: string;
  layout: string;
}

interface Template {
  template_id: number;
  template_name: string;
  template_type: string;
  template_content: string;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  // UI specific properties
  settings?: TemplateSettings;
  isDefault?: boolean;
  previewUrl?: string;
}

// Font options
const fontOptions = [
  { label: 'Inter', value: 'Inter' },
  { label: 'Roboto', value: 'Roboto' },
  { label: 'Open Sans', value: 'Open Sans' },
  { label: 'Lato', value: 'Lato' },
  { label: 'Montserrat', value: 'Montserrat' }
];

// Layout options
const layoutOptions = [
  { label: 'Standard', value: 'standard' },
  { label: 'Modern', value: 'modern' },
  { label: 'Compact', value: 'compact' },
  { label: 'Detailed', value: 'detailed' }
];

// Default settings if none available from API
const defaultSettings: TemplateSettings = {
  primaryColor: '#3B82F6',
  secondaryColor: '#93C5FD',
  font: 'Inter',
  logo: 'https://via.placeholder.com/200x60?text=Your+Logo',
  headerContent: 'Your Company Name\nAddress Line 1\nCity, State, Zip\nPhone: (123) 456-7890',
  footerContent: 'Thank you for your business!\nPayment terms: Net 30 days\nLate payments subject to 1.5% monthly interest.',
  layout: 'standard'
};

export const TemplateCustomization: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [template, setTemplate] = useState<Template | null>(null);
  const [settings, setSettings] = useState<TemplateSettings>(defaultSettings);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [activeIndex, setActiveIndex] = useState<number>(0);

  useEffect(() => {
    if (id) {
      fetchTemplate(id);
    }
  }, [id, dispatch]);

  const fetchTemplate = async (templateId: string) => {
    try {
      setIsLoading(true);
      const response = await getTemplateById(dispatch, templateId);

      if (response?.data) {
        // Transform API response
        const templateData = response.data;

        // In a real app, settings would come from the API
        // For this demo, we'll parse the settings if they exist in template_content,
        // or use default settings
        let templateSettings = defaultSettings;
        try {
          // This is a simplified approach - in a real app, the API would return structured settings
          if (templateData.template_content) {
            const settingsMatch = templateData.template_content.match(/<!-- SETTINGS: (.*?) -->/);
            if (settingsMatch && settingsMatch[1]) {
              templateSettings = JSON.parse(settingsMatch[1]);
            }
          }
        } catch (e) {
          console.error('Error parsing template settings:', e);
        }

        setTemplate({
          ...templateData,
          settings: templateSettings,
          isDefault: templateData.is_system,
          // Placeholder image for demo purposes
          previewUrl: `https://example.com/templates/${templateData.template_id}/preview.png`
        });

        setSettings(templateSettings);
        setPreviewUrl(`https://example.com/templates/${templateData.template_id}/preview.png`);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load template data. Please try again.',
        life: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingsChange = (field: keyof TemplateSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));

    // In a real app, this would trigger a live preview update
    // For demo purposes, we'll just simulate a refresh
    setTimeout(() => {
      setPreviewUrl(`https://example.com/templates/${id}/preview.png?t=${Date.now()}`);
    }, 500);
  };

  const handleLogoUpload = async (event: any) => {
    if (!id || !template) return;

    try {
      const file = event.files[0];

      // Call the API to upload the logo
      const response = await uploadTemplateLogo(dispatch, id, file);

      if (response) {
        // Update the logo URL in the settings
        const logoUrl = response.data?.logoUrl || settings.logo;
        handleSettingsChange('logo', logoUrl);

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Logo uploaded successfully',
          life: 3000
        });
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to upload logo. Please try again.',
        life: 3000
      });
    }
  };

  const handleSaveTemplate = async () => {
    if (!id || !template) return;

    setIsSaving(true);

    try {
      // Prepare settings object for API
      const settingsPayload = {
        primaryColor: settings.primaryColor,
        secondaryColor: settings.secondaryColor,
        font: settings.font,
        headerContent: settings.headerContent,
        footerContent: settings.footerContent,
        layout: settings.layout
      };

      // Update template settings via API
      const response = await updateTemplateSettings(dispatch, id, settingsPayload);

      if (response?.data) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Template settings saved successfully',
          life: 3000
        });

        // Update template in state
        setTemplate(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            settings: settings
          };
        });
      }
    } catch (error) {
      console.error('Error saving template settings:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save template settings. Please try again.',
        life: 3000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetAsDefault = async () => {
    if (!id || !template) return;

    try {
      // Make API call to set as default
      const response = await setDefaultTemplate(dispatch, id);

      if (response?.data) {
        // Update template in state
        setTemplate(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            isDefault: true
          };
        });

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Template set as default',
          life: 3000
        });
      }
    } catch (error) {
      console.error('Error setting template as default:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to set template as default. Please try again.',
        life: 3000
      });
    }
  };

  const generateTestInvoice = () => {
    // In a real app, this would generate a test invoice with the template
    toast.current?.show({
      severity: 'info',
      summary: 'Info',
      detail: 'Generating test invoice PDF...',
      life: 3000
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-6">
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
        <span className="ml-3">Loading template data...</span>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-medium text-gray-700">Template not found</h2>
        <p className="text-gray-600 mt-2">The requested template could not be found or you don't have permission to view it.</p>
        <Button
          className="mt-4"
          onClick={() => navigate('/templates')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />{"  "}
          Back to Templates
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Toast ref={toast} />

      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-${AppTheme.font.size['2xl']} font-bold text-gray-800`}>
          Customize Template - {template.template_name}
          {template.isDefault && (
            <span className="ml-2 bg-primary text-white px-2 py-1 rounded-full text-xs font-medium">
              Default
            </span>
          )}
        </h1>

        <div className="flex gap-2">
          <Button
            className="p-button-outlined"
            onClick={() => navigate('/templates')}
            >
            <FontAwesomeIcon icon={faArrowLeft} />{"  "}
            Back to Templates
          </Button>
          {!template.isDefault && (
            <Button
              className="p-button-outlined"
              onClick={handleSetAsDefault}
              >
              <FontAwesomeIcon icon={faCheckCircle} />{"  "}
              Set as Default
            </Button>
          )}
          <Button
            onClick={handleSaveTemplate}
            isLoading={isSaving}
            >
            <FontAwesomeIcon icon={faFloppyDisk} />{"  "}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left column - Customization controls */}
        <div className="md:col-span-2">
          <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
            <TabPanel header="Appearance">
              <Card>
                <div className="p-fluid p-4">
                  <h2 className="text-xl font-semibold mb-4">Colors and Fonts</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="field">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                      <div className="flex items-center">
                        <ColorPicker
                          value={settings.primaryColor.replace('#', '')}
                          onChange={(e) => handleSettingsChange('primaryColor', '#' + e.value)}
                          inline={false}
                          format="hex"
                        />
                        <span className="ml-4">{settings.primaryColor}</span>
                      </div>
                    </div>

                    <div className="field">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                      <div className="flex items-center">
                        <ColorPicker
                          value={settings.secondaryColor.replace('#', '')}
                          onChange={(e) => handleSettingsChange('secondaryColor', '#' + e.value)}
                          inline={false}
                          format="hex"
                        />
                        <span className="ml-4">{settings.secondaryColor}</span>
                      </div>
                    </div>

                    <div className="field">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Font</label>
                      <Dropdown
                        value={settings.font}
                        options={fontOptions}
                        onChange={(e) => handleSettingsChange('font', e.value)}
                        placeholder="Select Font"
                      />
                    </div>

                    <div className="field">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Layout</label>
                      <Dropdown
                        value={settings.layout}
                        options={layoutOptions}
                        onChange={(e) => handleSettingsChange('layout', e.value)}
                        placeholder="Select Layout"
                      />
                    </div>
                  </div>

                  <Divider />

                  <h2 className="text-xl font-semibold mb-4">Logo</h2>

                  <div className="mb-4">
                    <img
                      src={settings.logo}
                      alt="Company Logo"
                      className="h-16 max-w-xs mb-4 border border-gray-200 p-2"
                    />

                    <FileUpload
                      mode="basic"
                      name="logo"
                      url={`/templates/${id}/logo`}
                      accept="image/*"
                      maxFileSize={1000000}
                      chooseLabel="Change Logo"
                      onUpload={handleLogoUpload}
                      auto
                    />
                    <small className="text-gray-600 block mt-1">
                      Recommended size: 200x60px. Maximum file size: 1MB.
                    </small>
                  </div>
                </div>
              </Card>
            </TabPanel>

            <TabPanel header="Content">
              <Card>
                <div className="p-fluid p-4">
                  <h2 className="text-xl font-semibold mb-4">Header and Footer</h2>

                  <div className="field mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Header Content</label>
                    <InputTextarea
                      value={settings.headerContent}
                      onChange={(e) => handleSettingsChange('headerContent', e.target.value)}
                      rows={5}
                    />
                    <small className="text-gray-600">
                      This content will appear in the header of your invoices. You can include your company name, address, etc.
                    </small>
                  </div>

                  <div className="field">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Footer Content</label>
                    <InputTextarea
                      value={settings.footerContent}
                      onChange={(e) => handleSettingsChange('footerContent', e.target.value)}
                      rows={5}
                    />
                    <small className="text-gray-600">
                      This content will appear in the footer of your invoices. You can include payment terms, thank you notes, etc.
                    </small>
                  </div>
                </div>
              </Card>
            </TabPanel>
          </TabView>
        </div>

        {/* Right column - Preview */}
        <div>
          <Card className="sticky top-4">
            <div className="p-4">
              <h2 className="text-xl font-semibold mb-4">Live Preview</h2>

              <div className="border border-gray-200 rounded-md overflow-hidden">
                <img
                  src={previewUrl}
                  alt="Template Preview"
                  className="w-full h-auto"
                  style={{ minHeight: '400px' }}
                />
              </div>

              <div className="mt-4">
                <p className="text-gray-600 text-sm mb-4">
                  This is a simplified preview. Some details may appear different in the actual invoice.
                </p>

                <Button
                  className="p-button-outlined w-full"
                  onClick={generateTestInvoice}
                  >
                  <FontAwesomeIcon icon={faFilePdf} />{"  "}
                  Generate Test Invoice
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};